/**
 * Benefits Progress Bar Helper
 *
 * Calculates progress percentage for the Benefits Module progress bar
 * based on member's progression to the next tier.
 *
 * Logic mirrors the Nights Arc implementation:
 * - Calculates percentage of nights completed towards next tier
 * - Handles all member tiers (R, S, G, P, PPC, PFS)
 * - Returns progress as percentage (0-100)
 */

import { EARNINGS_SUMMARY_CONSTANTS, AmbassadorMemberCode } from './constants/constants';
import { getMemberLevelPropertiesByCode } from './helper';
import clsx from 'clsx';
import { baseVariables } from '@marriott/mi-ui-library';

export interface ProgressBarData {
  progressPercentage: number;
  currentNights: number;
  nightsToNextTier: number;
  nightsRequired: number;
  currentTier: string;
  nextTier: string;
}

/**
 * Tier progression mapping - uses EARNINGS_SUMMARY_CONSTANTS
 * Lazy-loaded to avoid initialization issues when module is imported before constants are ready
 */
const getTierProgression = () => {
  const tierNightRequirements = EARNINGS_SUMMARY_CONSTANTS.TIER_NIGHT_REQUIREMENTS;

  return {
    R: { next: 'S', nightsRequired: tierNightRequirements['S'] },
    S: { next: 'G', nightsRequired: tierNightRequirements['G'] },
    G: { next: 'P', nightsRequired: tierNightRequirements['P'] },
    P: { next: 'PPC', nightsRequired: tierNightRequirements['PPC'] },
    PPC: { next: AmbassadorMemberCode, nightsRequired: tierNightRequirements[AmbassadorMemberCode] },
    [AmbassadorMemberCode]: { next: null, nightsRequired: null }, // Ambassador is max tier
  } as const;
};

// Lazy-loaded tier progression to avoid initialization issues
// Values are computed on first access, ensuring EARNINGS_SUMMARY_CONSTANTS is available
let _tierProgression: ReturnType<typeof getTierProgression> | null = null;
const TIER_PROGRESSION = () => {
  if (!_tierProgression) {
    _tierProgression = getTierProgression();
  }
  return _tierProgression;
};

/**
 * Calculates the progress percentage for benefits module
 *
 * Formula: (currentNights / nightsRequiredForNextTier) * 100
 *
 * @param currentTierCode - Current member tier code (R, S, G, P, PPC, PFS)
 * @param currentNights - Total nights stayed this year
 * @returns ProgressBarData object with percentage and details
 *
 * @example
 * ```typescript
 * // Brand new member with 0 nights
 * const progress = calculateBenefitsProgress('R', 0);
 * // Returns: { progressPercentage: 0, currentNights: 0, nightsToNextTier: 10, ... }
 *
 * // Silver member with 5 nights towards Gold
 * const progress = calculateBenefitsProgress('S', 5);
 * // Returns: { progressPercentage: 20, currentNights: 5, nightsToNextTier: 20, ... }
 *
 * // Gold member with 30 nights towards Platinum
 * const progress = calculateBenefitsProgress('G', 30);
 * // Returns: { progressPercentage: 60, currentNights: 30, nightsToNextTier: 25, ... }
 * ```
 */
export const calculateBenefitsProgress = (
  currentTierCode: string | undefined,
  currentNights: number = 0
): ProgressBarData => {
  const tierProgression = TIER_PROGRESSION();
  const tierCode = (currentTierCode || 'R') as keyof ReturnType<typeof TIER_PROGRESSION>;
  const tierInfo = tierProgression[tierCode];

  // If at max tier (Ambassador), return 100%
  if (!tierInfo || !tierInfo.next) {
    return {
      progressPercentage: 100,
      currentNights,
      nightsToNextTier: 0,
      nightsRequired: 0,
      currentTier: tierCode,
      nextTier: tierCode,
    };
  }

  // Get the nights required for NEXT tier (the absolute target)
  const nextTierNights = tierInfo.nightsRequired;

  // Calculate ABSOLUTE progress: currentNights / nextTierNights (from 0 to max tier requirement)
  // This shows progress as a percentage of the journey from Member (0) to Ambassador (100)
  const progressPercentage = Math.min((currentNights / nextTierNights) * 100, 100);

  return {
    progressPercentage: Math.round(progressPercentage),
    currentNights,
    nightsToNextTier: Math.max(nextTierNights - currentNights, 0),
    nightsRequired: nextTierNights,
    currentTier: tierCode,
    nextTier: tierInfo.next,
  };
};

/**
 * Gets the progress bar display text
 *
 * @param progressData - ProgressBarData from calculateBenefitsProgress
 * @returns Display text for the progress bar
 *
 * @example
 * ```typescript
 * const progress = calculateBenefitsProgress('S', 5);
 * const text = getProgressBarText(progress);
 * // Returns: "5 of 25 nights to Gold Elite"
 * ```
 */
export const getProgressBarText = (progressData: ProgressBarData): string => {
  if (progressData.currentTier === AmbassadorMemberCode) {
    return `${EARNINGS_SUMMARY_CONSTANTS.TIER_NAMES[AmbassadorMemberCode]} - Max Tier`;
  }

  const nextTierName =
    EARNINGS_SUMMARY_CONSTANTS.TIER_NAMES[
      progressData.nextTier as keyof typeof EARNINGS_SUMMARY_CONSTANTS.TIER_NAMES
    ] || progressData.nextTier;
  return `${progressData.currentNights} of ${progressData.nightsRequired} nights to ${nextTierName}`;
};

/**
 * Gets the progress bar color based on tier
 * Uses getMemberLevelPropertiesByCode to get tier-specific colors
 *
 * @param tierCode - Current member tier code
 * @param memberStatusList - Optional array of member status configurations
 * @returns Color code for the progress bar
 */
export const getProgressBarColor = (
  tierCode: string | undefined,
  memberStatusList?: Array<{ id: string; label: string }>
): string => {
  if (!tierCode || !memberStatusList) {
    return '#E8A537'; // Default Member orange
  }

  const memberProperties = getMemberLevelPropertiesByCode(tierCode, memberStatusList);
  return memberProperties.borderLeft || '#E8A537';
};

/**
 * Determines if progress bar should be shown
 *
 * @param tierCode - Current member tier code
 * @param currentNights - Total nights stayed
 * @returns boolean indicating if progress bar should be visible
 */
export const shouldShowProgressBar = (tierCode: string | undefined): boolean => {
  // Don't show for Ambassador Elite (max tier)
  return tierCode !== AmbassadorMemberCode;
};

interface ProgressBarRenderProps {
  index: number;
  memberLevels: Array<{ id: string; label: string }>;
  userActualTier: string | undefined;
  achievedTierIndex: number;
  isMobileViewPort: boolean;
  memberProperties: { borderLeft?: string } | undefined;
  getProgressForTier: (tierCode: string) => number;
}

interface ProgressBarResult {
  className: string;
  style?: React.CSSProperties;
}

/**
 * Renders the progress bar between tiers with proper styling and progress calculation
 *
 * @param props - All the necessary props for rendering progress bar
 * @returns Object with className and style for the progress bar span
 */
export const renderProgressBar = (props: ProgressBarRenderProps): ProgressBarResult => {
  const {
    index,
    memberLevels,
    userActualTier,
    achievedTierIndex,
    isMobileViewPort,
    memberProperties,
    getProgressForTier,
  } = props;

  // Don't render progress bar after the last tier
  if (index >= memberLevels.length - 1) {
    return { className: '' };
  }

  const userActualTierIndex = memberLevels?.findIndex((tier: { id: string }) => tier.id === userActualTier);

  /**
   * Calculate which line should display progress
   * Desktop: Array descending [PFS→R], line renders below tier (index - 1)
   * Mobile: Array ascending [R→PFS], line renders after tier (same index)
   * @example Desktop Silver(4) → line 3 | Mobile Silver(1) → line 1
   */
  const isLineBeforeActualTier = isMobileViewPort ? index === userActualTierIndex : index === userActualTierIndex - 1;

  const progress = isLineBeforeActualTier && userActualTier ? getProgressForTier(userActualTier) : 0;

  /**
   * Determine if line should be fully filled (stepper-active)
   * Desktop: Lines at/above achieved tier (>=)
   * Mobile: Lines at/before achieved tier (<=)
   */
  const isAchievedLine =
    (!isMobileViewPort && achievedTierIndex !== -1 && index >= achievedTierIndex) ||
    (isMobileViewPort && achievedTierIndex !== -1 && index <= achievedTierIndex);

  const className = clsx(
    'slide-separator',
    // Only apply stepper-active if there's NO progress AND line is achieved
    progress === 0 && isAchievedLine ? 'stepper-active' : ''
  );

  const style =
    progress > 0
      ? {
          background: `linear-gradient(${isMobileViewPort ? 'to right' : 'to top'}, ${
            memberProperties?.borderLeft
          } 0%, ${memberProperties?.borderLeft} ${progress}%, ${baseVariables.color.neutral30} ${progress}%, ${
            baseVariables.color.neutral30
          } 100%)`,
        }
      : undefined;

  return {
    className,
    style,
  };
};

/**
 * Gets milestone information for progress bar
 *
 * @returns Array of milestone percentages for visual markers
 */
export const getProgressMilestones = (): number[] => {
  // Return quartile milestones (25%, 50%, 75%, 100%)
  return [25, 50, 75, 100];
};
